
<p-confirmDialog />

<div class="card">
  <div class="card-content">

    <!-- Seed defaults prompt shown only if table is empty -->
    <div *ngIf="!loading && templates.length === 0" class="seed-bar">
      <p>No offboarding templates configured. Seed the default BISTEC Global offboarding tasks?</p>
      <p-button label="Seed Default Templates" icon="pi pi-download" severity="primary" (onClick)="seedDefaults()" />
    </div>

    <!-- ============ Tab bar ============ -->
    <div *ngIf="!loading && templates.length > 0" class="ot-tabbar" role="tablist">
      <button
        *ngFor="let t of tabs"
        type="button"
        role="tab"
        [attr.aria-selected]="activeTab === t.key"
        class="ot-tab"
        [class.ot-tab--active]="activeTab === t.key"
        (click)="setTab(t.key)">
        <span class="ot-tab-label">{{ t.label }}</span>
        <span class="ot-tab-count">{{ getTabCount(t.key) }}</span>
      </button>
    </div>

    <!-- ============ Per-tab toolbar: search + status + bulk-delete + add ============ -->
    <div *ngIf="!loading && templates.length > 0" class="ot-toolbar">
      <div class="ot-toolbar-left">
        <span class="ot-search">
          <i class="pi pi-search"></i>
          <input pInputText type="text"
            [placeholder]="'Search ' + activeTabLabel + ' tasks…'"
            [(ngModel)]="tabSearch" />
        </span>
        <p-select
          [(ngModel)]="tabStatusFilter"
          [options]="statusFilterOptions"
          optionLabel="label"
          optionValue="value"
          styleClass="ot-status-filter"
          appendTo="body" />
      </div>
      <div class="ot-toolbar-right">
        <p-button
          *ngIf="selectedIds.length > 0"
          [label]="'Delete Selected (' + selectedIds.length + ')'"
          icon="pi pi-trash"
          severity="danger"
          [text]="true"
          [loading]="deletingBulk"
          (onClick)="deleteSelected()" />
        <p-button
          *ngIf="selectedIds.length > 0"
          label="Clear"
          icon="pi pi-times"
          severity="secondary"
          [text]="true"
          (onClick)="clearSelection()" />
        <p-button
          [label]="'Add ' + activeTabLabel + ' Task'"
          icon="pi pi-plus"
          severity="primary"
          (onClick)="showAddDialog()" />
      </div>
    </div>

    <!-- ============ Reorderable task table for the active tab ============ -->
    <p-table
      *ngIf="!loading && templates.length > 0"
      [value]="currentTabTasks"
      [loading]="reordering"
      (onRowReorder)="onRowReorder($event)"
      [tableStyle]="{ 'min-width': '100%' }"
      styleClass="p-datatable no-column-lines ot-task-table">

      <ng-template pTemplate="header">
        <tr>
          <th style="width: 2.5rem" class="ot-drag-cell"></th>
          <th style="width: 2.5rem" class="text-center">
            <p-checkbox
              [binary]="true"
              [ngModel]="isAllVisibleSelected()"
              (ngModelChange)="toggleSelectAllVisible($event)"
              pTooltip="Select all visible" tooltipPosition="top" />
          </th>
          <th>Task Name</th>
          <th class="th-actions" style="width: 130px">Actions</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-template let-index="rowIndex">
        <tr [pReorderableRow]="index" [class.ot-row-selected]="isSelected(template.id)"
            [class.row-inactive]="template.isDefault === false">
          <td class="ot-drag-cell">
            <span class="pi pi-bars ot-drag-handle" pReorderableRowHandle
                  pTooltip="Drag to reorder" tooltipPosition="right"></span>
          </td>
          <td class="text-center">
            <p-checkbox
              [binary]="true"
              [ngModel]="isSelected(template.id)"
              (ngModelChange)="toggleSelect(template.id)" />
          </td>
          <td class="td-truncate" [pTooltip]="template.taskName" tooltipPosition="top">
            {{ template.taskName }}
          </td>
          <td class="table-cell-actions">
            <div class="table-actions-stack">
              <p-button
                [icon]="template.isDefault ? 'pi pi-check-circle' : 'pi pi-times-circle'"
                [severity]="template.isDefault ? 'success' : 'secondary'"
                [text]="true" size="small"
                [pTooltip]="template.isDefault ? 'Active — click to deactivate' : 'Inactive — click to activate'"
                tooltipPosition="top" (onClick)="toggleActive(template)" />
              <p-button icon="pi pi-pencil" [text]="true" severity="info" size="small"
                pTooltip="Edit" tooltipPosition="top" (onClick)="showEditDialog(template)" />
              <p-button icon="pi pi-trash" [text]="true" severity="danger" size="small"
                pTooltip="Delete" tooltipPosition="top" (onClick)="deleteTemplate(template)" />
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr class="table-empty-row">
          <td colspan="4" class="table-empty-cell">
            <div class="empty-state">
              <div class="empty-icon"><i class="pi pi-inbox"></i></div>
              <p class="empty-message">
                No {{ activeTabLabel }} tasks
                <ng-container *ngIf="tabSearch || tabStatusFilter !== 'all'"> match this filter</ng-container>
              </p>
              <p class="empty-description">
                <ng-container *ngIf="!tabSearch && tabStatusFilter === 'all'">
                  Click "Add {{ activeTabLabel }} Task" to create one
                </ng-container>
                <ng-container *ngIf="tabSearch || tabStatusFilter !== 'all'">
                  Clear the search or status filter to see all {{ activeTabLabel }} tasks
                </ng-container>
              </p>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>

    <!-- Loading state -->
    <div *ngIf="loading" class="ot-loading">
      <i class="pi pi-spin pi-spinner"></i>
      <span>Loading templates…</span>
    </div>
  </div>
</div>

<!-- ============ Add / Edit dialog ============ -->
<p-dialog
  [visible]="displayDialog"
  (visibleChange)="displayDialog = $event"
  [header]="isEditMode ? 'Edit Task Template' : ('Add ' + activeTabLabel + ' Task')"
  [modal]="true"
  styleClass="dialog-md"
  [dismissableMask]="true"
  [closable]="true">

  <div class="form-dialog-form">
    <div class="form-dialog-container">

      <!-- Department — hidden in add mode when the tab has only one department -->
      <div class="form-row" *ngIf="isEditMode || dialogDepartmentOptions.length > 1">
        <div class="field">
          <label for="department">Department <span class="required-star">*</span></label>
          <p-select id="department" [(ngModel)]="selectedTemplate.department" [options]="dialogDepartmentOptions"
            optionLabel="label" optionValue="value" placeholder="Select department" appendTo="body" [filter]="true" filterPlaceholder="Search…" />
        </div>
      </div>

      <div class="form-row">
        <div class="field" [class.field-invalid]="fieldTouched['taskName'] && !selectedTemplate.taskName.trim()">
          <label for="taskName">Task Name <span class="required-star">*</span></label>
          <textarea id="taskName" [(ngModel)]="selectedTemplate.taskName"
            [rows]="2" class="task-textarea"
            placeholder="e.g. Get Employee Resignation Letter"
            (blur)="fieldTouched['taskName'] = true"></textarea>
          <small class="field-error" *ngIf="fieldTouched['taskName'] && !selectedTemplate.taskName.trim()">Required</small>
        </div>
      </div>

      <div class="form-row">
        <div class="field">
          <label>Status</label>
          <div class="status-toggle">
            <p-checkbox [(ngModel)]="selectedTemplate.isDefault" [binary]="true" inputId="isDefault" />
            <label for="isDefault">Active — include when offboarding starts</label>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="form-dialog-footer-btns">
      <p-button label="Cancel" icon="pi pi-times" (onClick)="hideDialog()" severity="secondary" [outlined]="true" />
      <p-button label="Save" icon="pi pi-check" (onClick)="saveTemplate()" severity="primary" />
    </div>
  </ng-template>
</p-dialog>
